import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@shared/api';

export interface AssistSource {
  title: string;
  url: string;
}
export interface AssistAnswer {
  answer: string;
  sources: AssistSource[];
  remaining_today: number;
}
export interface AssistStatus {
  enabled: boolean;
  remaining_today: number;
  daily_limit: number;
}

export type AssistState =
  | { phase: 'idle' }
  | { phase: 'loading'; question: string }
  | { phase: 'done'; question: string; result: AssistAnswer }
  | { phase: 'error'; question: string; message: string; retry: boolean };

let statusCache: AssistStatus | null = null;

export function useAssistStatus(enabled: boolean): AssistStatus | null {
  const [status, setStatus] = useState<AssistStatus | null>(statusCache);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    api<AssistStatus>('/v1/assist/status')
      .then((next) => {
        statusCache = next;
        if (alive) setStatus(next);
      })
      .catch(() => alive && setStatus(null));
    return () => {
      alive = false;
    };
  }, [enabled]);
  return status;
}

function friendly(err: unknown): { message: string; retry: boolean } {
  if (err instanceof ApiError && err.status === 429) return { message: 'Jatah Tanya AI hari ini sudah habis. Coba lagi besok, atau cari langsung di dokumentasi.', retry: false };
  if (err instanceof ApiError && err.status === 503) return { message: 'Tanya AI sedang tidak tersedia. Coba lagi sebentar lagi.', retry: true };
  return { message: 'Jawaban gagal dimuat. Periksa koneksi Anda lalu coba lagi.', retry: true };
}

export function useAssist(onRemaining?: (remaining: number) => void) {
  const [state, setState] = useState<AssistState>({ phase: 'idle' });
  const seq = useRef(0);

  const ask = useCallback(
    async (question: string, path: string) => {
      const id = ++seq.current;
      setState({ phase: 'loading', question });
      try {
        const result = await api<AssistAnswer>('/v1/assist/answer', { method: 'POST', body: { question, path } });
        if (statusCache) statusCache = { ...statusCache, remaining_today: result.remaining_today };
        onRemaining?.(result.remaining_today);
        if (id === seq.current) setState({ phase: 'done', question, result });
      } catch (err) {
        // quota spent
        if (err instanceof ApiError && err.status === 429) {
          if (statusCache) statusCache = { ...statusCache, remaining_today: 0 };
          onRemaining?.(0);
        }
        if (id === seq.current) setState({ phase: 'error', question, ...friendly(err) });
      }
    },
    [onRemaining],
  );

  const reset = useCallback(() => {
    seq.current++;
    setState({ phase: 'idle' });
  }, []);

  return { state, ask, reset };
}
